import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

const AdminLogin = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate("/admin");
    } catch (e2) {
      const msg = formatError(e2.response?.data?.detail) || "Login gagal";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center bg-[#0F172A] relative overflow-hidden"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 kt-grid-bg opacity-10" />

      <div className="relative w-full max-w-md mx-4">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6"
          data-testid="admin-login-back-home"
        >
          <ArrowLeft size={16} /> Kembali ke Website
        </a>

        <div className="bg-white border border-[#E4E4E7] p-8 md:p-10">
          <Logo size={42} />
          <div className="kt-divider-red mt-6" />
          <h1 className="font-display font-bold text-2xl mt-6">Login Admin</h1>
          <p className="text-sm text-[#52525B] mt-2">
            Masuk untuk mengelola layanan, portfolio, dan inquiry.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#18181B] mb-2 block">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@komunikateknik.online"
                className="w-full border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
                data-testid="admin-login-email-input"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#18181B] mb-2 block">
                Password
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
                data-testid="admin-login-password-input"
              />
            </label>

            {err && (
              <div
                className="text-sm text-[#DC2626] border-l-2 border-[#DC2626] pl-3"
                data-testid="admin-login-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="kt-btn-primary w-full justify-center disabled:opacity-60"
              data-testid="admin-login-submit-button"
            >
              {submitting ? "Memproses…" : "Masuk"}
              {!submitting && <LogIn size={16} />}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E4E4E7] flex items-center gap-2 text-xs text-[#52525B] font-mono-kt">
            <Lock size={14} /> Koneksi aman · JWT Auth
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
